<script setup lang="ts">
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { useStaffStore } from '../stores/staffStore'
import type { IStaffEmployee } from '../types/IStaffEmployee'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    employeeIds: string[]
    employees?: IStaffEmployee[]
  }>(),
  {
    employees: () => [],
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:employeeIds', value: string[]): void
  (e: 'saved'): void
}>()

const staffStore = useStaffStore()
const permissionsStore = usePermissionsStore()
const feedbackStore = useFeedbackStore()

const isOpen = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
})

const activeTab = ref<'add' | 'delete'>('add')
const isConfirmExitOpen = ref(false)

// Локальные выбранные роли для чекбоксов
const checkedRoles = ref<string[]>([])

// Для массового редактирования (bulk)
const rolesToAdd = ref<string[]>([])
const rolesToRemove = ref<string[]>([])

// Для единичного редактирования (single)
const initialSingleRoles = ref<string[]>([])

// Локальный список редактируемых сотрудников
const localEmployeeIds = ref<string[]>([])

// Сохраняем загруженные детали сотрудника при единичном редактировании
const singleEmployee = ref<IStaffEmployee | null>(null)

// Состояние
const isBulk = computed(() => localEmployeeIds.value.length > 1)

const singleEmployeeName = computed(() => {
  if (isBulk.value || localEmployeeIds.value.length === 0) return ''
  const id = localEmployeeIds.value[0]
  const emp = singleEmployee.value
    || props.employees.find(e => e.id === id)
    || staffStore.staffList.find(e => e.id === id)
  if (!emp) return ''
  return emp.full_name || ''
})

const selectedEmployeesList = computed(() => {
  return localEmployeeIds.value.map(id => {
    const emp = (id === singleEmployee.value?.id ? singleEmployee.value : null)
      || props.employees.find(e => e.id === id)
      || staffStore.staffList.find(e => e.id === id)
    return emp || { id, full_name: 'Неизвестный Сотрудник', avatar: null, position: null }
  })
})

const selectedItemsData = computed(() => {
  return selectedEmployeesList.value.map(emp => ({
    id: emp.id,
    name: emp.full_name || '',
    avatar: getEmployeeAvatar(emp),
    description: emp.position?.name || 'Должность',
  }))
})

function getEmployeeAvatar(emp: IStaffEmployee | { avatar?: string | null } | null | undefined) {
  if (emp && emp.avatar) {
    return emp.avatar
  }
  return getRandomAvatar()
}

function removeEmployee(id: string) {
  localEmployeeIds.value = localEmployeeIds.value.filter(empId => empId !== id)
  emit('update:employeeIds', [...localEmployeeIds.value])

  if (localEmployeeIds.value.length === 0) {
    closeWithoutSave()
    return
  }

  if (localEmployeeIds.value.length === 1) {
    const remainingId = localEmployeeIds.value[0]
    // Единичное редактирование: берем текущие роли оставшегося сотрудника
    let emp = props.employees.find(e => e.id === remainingId) || staffStore.staffList.find(e => e.id === remainingId)
    if (!emp) {
      staffStore.fetchEmployeeById(remainingId).then(fetchedEmp => {
        if (fetchedEmp) {
          singleEmployee.value = fetchedEmp
          initialSingleRoles.value = (fetchedEmp.roles || []).map(r => r.sub)
          checkedRoles.value = [...initialSingleRoles.value]
          rolesToAdd.value = []
          rolesToRemove.value = []
        }
      })
    } else {
      singleEmployee.value = emp
      initialSingleRoles.value = (emp.roles || []).map(r => r.sub)
      checkedRoles.value = [...initialSingleRoles.value]
      rolesToAdd.value = []
      rolesToRemove.value = []
    }
  }
}

function removeAllEmployees() {
  localEmployeeIds.value = []
  emit('update:employeeIds', [])
  checkedRoles.value = []
  initialSingleRoles.value = []
  rolesToAdd.value = []
  rolesToRemove.value = []
  singleEmployee.value = null
  closeWithoutSave()
}

// Инициализация при открытии модалки
watch(
  () => props.modelValue,
  async newVal => {
    if (newVal) {
      localEmployeeIds.value = [...props.employeeIds]
      activeTab.value = 'add'
      rolesToAdd.value = []
      rolesToRemove.value = []
      checkedRoles.value = []
      initialSingleRoles.value = []
      singleEmployee.value = null

      try {
        if (staffStore.rolesList.length === 0) {
          await staffStore.fetchRoles()
        }

        if (localEmployeeIds.value.length === 1) {
          const id = localEmployeeIds.value[0]
          let emp = props.employees.find(e => e.id === id) || staffStore.staffList.find(e => e.id === id)
          if (!emp) {
            const fetchedEmp = await staffStore.fetchEmployeeById(id)
            if (fetchedEmp) {
              emp = fetchedEmp
            }
          }
          if (emp) {
            singleEmployee.value = emp
            initialSingleRoles.value = (emp.roles || []).map(r => r.sub)
            checkedRoles.value = [...initialSingleRoles.value]
          }
        }
      } catch (err) {
        console.error('Ошибка инициализации ролей:', err)
      }
    }
  },
)

// Синхронизация checkedRoles с rolesToAdd / rolesToRemove в зависимости от вкладки в массовом режиме
watch(checkedRoles, newChecked => {
  if (!isBulk.value) return

  if (activeTab.value === 'add') {
    rolesToAdd.value = [...newChecked]
  } else {
    rolesToRemove.value = [...newChecked]
  }
})

// Переключение вкладок в массовом режиме — синхронизация чекбоксов с rolesToAdd / rolesToRemove
function syncCheckedRolesForTab(tab: 'add' | 'delete') {
  checkedRoles.value = tab === 'add' ? [...rolesToAdd.value] : [...rolesToRemove.value]
}

function onBulkTabChange(tab: unknown) {
  syncCheckedRolesForTab(tab as 'add' | 'delete')
}

// Хелперы клика по тексту роли
function onRoleCheckToggle(roleName: string, checked: boolean | null) {
  if (!isBulk.value) {
    const index = checkedRoles.value.indexOf(roleName)
    if (checked) {
      if (index === -1) {
        checkedRoles.value = [...checkedRoles.value, roleName]
      }
    } else {
      if (index !== -1) {
        checkedRoles.value = checkedRoles.value.filter(r => r !== roleName)
      }
    }
    return
  }

  if (checked) {
    if (activeTab.value === 'add') {
      if (rolesToRemove.value.includes(roleName)) {
        feedbackStore.showToast({
          type: 'error',
          message: 'Нельзя одновременно добавить и удалить одну и ту же роль',
          time: 4000,
        })
        checkedRoles.value = [...checkedRoles.value, roleName]
        nextTick(() => {
          checkedRoles.value = checkedRoles.value.filter(r => r !== roleName)
        })
        return
      }
      if (!checkedRoles.value.includes(roleName)) {
        checkedRoles.value = [...checkedRoles.value, roleName]
      }
    } else {
      if (rolesToAdd.value.includes(roleName)) {
        feedbackStore.showToast({
          type: 'error',
          message: 'Нельзя одновременно добавить и удалить одну и ту же роль',
          time: 4000,
        })
        checkedRoles.value = [...checkedRoles.value, roleName]
        nextTick(() => {
          checkedRoles.value = checkedRoles.value.filter(r => r !== roleName)
        })
        return
      }
      if (!checkedRoles.value.includes(roleName)) {
        checkedRoles.value = [...checkedRoles.value, roleName]
      }
    }
  } else {
    checkedRoles.value = checkedRoles.value.filter(r => r !== roleName)
  }
}

// Разделение ролей на 2 колонки
const leftColumnRoles = computed(() => {
  const list = staffStore.rolesList || []
  const mid = Math.ceil(list.length / 2)
  return list.slice(0, mid)
})

const rightColumnRoles = computed(() => {
  const list = staffStore.rolesList || []
  const mid = Math.ceil(list.length / 2)
  return list.slice(mid)
})

// Описание ролей
function getRoleDescription(sub: string): string {
  const role = staffStore.rolesList.find(r => r.sub === sub)
  return role ? (role.name || role.sub) : sub
}

const hasAnySelected = computed(() => {
  return rolesToAdd.value.length > 0 || rolesToRemove.value.length > 0
})

// Проверка на наличие изменений
const hasChanges = (): boolean => {
  if (isBulk.value) {
    return rolesToAdd.value.length > 0 || rolesToRemove.value.length > 0
  } else {
    return JSON.stringify(checkedRoles.value.sort()) !== JSON.stringify(initialSingleRoles.value.sort())
  }
}

// Попытка закрыть форму
const handleClose = () => {
  if (hasChanges()) {
    isConfirmExitOpen.value = true
  } else {
    closeWithoutSave()
  }
}

const onDismissAttempt = useDialogDismissGuard(handleClose, 'RoleSettingsDialog')

function closeWithoutSave() {
  isConfirmExitOpen.value = false
  isOpen.value = false
}

/**
 * Сохраняет назначенные или измененные роли сотрудников с проверкой прав доступа (modules.employee_management.role_edit).
 */
async function saveChanges() {
  if (localEmployeeIds.value.length === 0) {
    closeWithoutSave()
    return
  }

  if (!permissionsStore.modules.employee_management?.role_edit) {
    feedbackStore.showToast({
      type: 'error',
      message: 'У вас нет прав на редактирование ролей',
      time: 5000,
    })
    return
  }

  try {
    if (isBulk.value) {
      // Массовое редактирование
      await staffStore.bulkUpdateEmployeeRoles(
        localEmployeeIds.value,
        rolesToAdd.value,
        rolesToRemove.value,
      )

      await staffStore.fetchStaff(undefined, staffStore.itemsPerPage)

      feedbackStore.showToast({
        type: 'success',
        message: `Роли успешно изменены для ${localEmployeeIds.value.length} сотрудников`,
        time: 4000,
      })
    } else {
      // Единичное редактирование
      const id = localEmployeeIds.value[0]
      const rolesPayload = checkedRoles.value.map(sub => ({ sub }))

      await staffStore.updateEmployee(id, {
        roles: rolesPayload,
      })

      await staffStore.fetchStaff(undefined, staffStore.itemsPerPage)

      feedbackStore.showToast({
        type: 'success',
        message: 'Роли сотрудника успешно обновлены',
        time: 4000,
      })
    }

    emit('saved')
    isOpen.value = false
  } catch (err) {
    console.error('Ошибка сохранения ролей:', err)
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(err, 'Не удалось сохранить изменения'),
      time: 5000,
    })
  }
}
</script>

<template>
  <VDialog
    v-model="isOpen"
    max-width="560"
    persistent
    no-click-animation
    @click:outside="onDismissAttempt"
    @keydown.esc.stop="onDismissAttempt"
  >
    <VCard class="role-settings-card pa-6 d-flex flex-column h-auto bg-surface rounded-lg">
      <!-- Шапка модалки -->
      <div class="d-flex justify-space-between align-center">
        <h3 class="text-h4 font-weight-medium text-high-emphasis ma-0">
          Настройка ролей
        </h3>
        <VIconBtn
          variant="text"
          icon="ri-close-line"
          color="secondary"
          @click="onDismissAttempt"
        />
      </div>

      <!-- Контент модалки -->
      <div class="pb-4 flex-grow-1">
        <div v-if="isBulk" class="mb-5">
          <VMenu
            open-on-hover
            :close-on-content-click="false"
            close-delay="200"
            location="bottom start"
            offset="8"
          >
            <template #activator="{ props: menuProps }">
              <span
                v-bind="menuProps"
                class="text-body-2 text-primary font-weight-medium cursor-pointer d-inline-block"
              >
                Выбрано пользователей: {{ localEmployeeIds.length }}
              </span>
            </template>

            <CustomSelectedItems
              :items="selectedItemsData"
              :title="`Выбрано ${localEmployeeIds.length}:`"
              @remove="id => removeEmployee(String(id))"
              @remove-all="removeAllEmployees"
            />
          </VMenu>
        </div>
        <p v-else class="text-body-2 text-medium-emphasis mb-4">
          Редактирование ролей для пользователя <strong class="text-high-emphasis font-weight-medium">{{ singleEmployeeName }}</strong>
        </p>

        <!-- Вкладки-переключатели (только для массового редактирования) -->
        <VTabs
          v-if="isBulk"
          v-model="activeTab"
          class="role-action-tabs"
          grow
          @update:model-value="onBulkTabChange"
        >
          <VTab value="add" class="role-action-tab role-action-tab--add">
            <VIcon icon="ri-add-line" size="18" class="me-1" />
            Добавить роли
          </VTab>
          <VTab value="delete" class="role-action-tab role-action-tab--delete">
            <VIcon icon="ri-subtract-line" size="18" class="me-1" />
            Удалить роли
          </VTab>
        </VTabs>

        <!-- Массовое редактирование: контент вкладок с анимацией листания -->
        <VWindow
          v-if="isBulk"
          v-model="activeTab"
          class="role-action-window"
          :touch="false"
          @update:model-value="onBulkTabChange"
        >
          <VWindowItem
            v-for="tabValue in (['add', 'delete'] as const)"
            :key="tabValue"
            :value="tabValue"
          >
            <!-- Список ролей с чекбоксами (в 2 колонки) -->
            <div class="roles-grid mb-5">
              <div class="role-column gap-2">
                <VCheckbox
                  v-for="role in leftColumnRoles"
                  :key="`${tabValue}-${role.sub}`"
                  :model-value="checkedRoles.includes(role.sub)"
                  color="primary"
                  hide-details
                  class="custom-checkbox"
                  @update:model-value="onRoleCheckToggle(role.sub, $event)"
                >
                  <template #label>
                    <VTooltip location="top" :disabled="!role.description">
                      <template #activator="{ props: tooltipProps }">
                        <span v-bind="tooltipProps">{{ role.name }}</span>
                      </template>
                      <span>{{ role.description }}</span>
                    </VTooltip>
                  </template>
                </VCheckbox>
              </div>
              <div class="role-column gap-2">
                <VCheckbox
                  v-for="role in rightColumnRoles"
                  :key="`${tabValue}-${role.sub}`"
                  :model-value="checkedRoles.includes(role.sub)"
                  color="primary"
                  hide-details
                  class="custom-checkbox"
                  @update:model-value="onRoleCheckToggle(role.sub, $event)"
                >
                  <template #label>
                    <VTooltip location="top" :disabled="!role.description">
                      <template #activator="{ props: tooltipProps }">
                        <span v-bind="tooltipProps">{{ role.name }}</span>
                      </template>
                      <span>{{ role.description }}</span>
                    </VTooltip>
                  </template>
                </VCheckbox>
              </div>
            </div>

            <!-- Нижний блок описания / чипсов -->
            <div class="action-summary-container border-t pt-2">
              <p v-if="!hasAnySelected" class="text-body-2 text-medium-emphasis m-0">
                <template v-if="tabValue === 'add'">
                  Выбранные вами роли будут <strong class="text-high-emphasis font-weight-semibold">добавлены</strong> {{ localEmployeeIds.length }} пользователям
                </template>
                <template v-else>
                  Выбранные вами роли будут <strong class="text-high-emphasis font-weight-semibold">удалены</strong> у {{ localEmployeeIds.length }} пользователей
                </template>
              </p>

              <div v-else class="d-flex flex-column gap-3">
                <div v-if="rolesToAdd.length > 0" class="d-flex flex-column">
                  <span class="text-body-2 font-weight-medium text-medium-emphasis">Добавить:</span>
                  <div class="d-flex flex-wrap gap-2 mt-1">
                    <span
                      v-for="roleName in rolesToAdd"
                      :key="roleName"
                      class="role-summary-chip text-caption font-weight-regular text-no-wrap py-1 px-3 rounded-pill"
                    >
                      {{ getRoleDescription(roleName) }}
                    </span>
                  </div>
                </div>

                <div v-if="rolesToRemove.length > 0" class="d-flex flex-column">
                  <span class="text-body-2 font-weight-medium text-medium-emphasis">Удалить:</span>
                  <div class="d-flex flex-wrap gap-2 mt-1">
                    <span
                      v-for="roleName in rolesToRemove"
                      :key="roleName"
                      class="role-summary-chip text-caption font-weight-regular text-no-wrap py-1 px-3 rounded-pill"
                    >
                      {{ getRoleDescription(roleName) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </VWindowItem>
        </VWindow>

        <!-- Единичное редактирование -->
        <div v-else class="roles-grid mb-5">
          <div class="role-column gap-2">
            <VCheckbox
              v-for="role in leftColumnRoles"
              :key="role.sub"
              :model-value="checkedRoles.includes(role.sub)"
              color="primary"
              hide-details
              class="custom-checkbox"
              @update:model-value="onRoleCheckToggle(role.sub, $event)"
            >
              <template #label>
                <VTooltip location="top" :disabled="!role.description">
                  <template #activator="{ props: tooltipProps }">
                    <span v-bind="tooltipProps">{{ role.name }}</span>
                  </template>
                  <span>{{ role.description }}</span>
                </VTooltip>
              </template>
            </VCheckbox>
          </div>
          <div class="role-column gap-2">
            <VCheckbox
              v-for="role in rightColumnRoles"
              :key="role.sub"
              :model-value="checkedRoles.includes(role.sub)"
              color="primary"
              hide-details
              class="custom-checkbox"
              @update:model-value="onRoleCheckToggle(role.sub, $event)"
            >
              <template #label>
                <VTooltip location="top" :disabled="!role.description">
                  <template #activator="{ props: tooltipProps }">
                    <span v-bind="tooltipProps">{{ role.name }}</span>
                  </template>
                  <span>{{ role.description }}</span>
                </VTooltip>
              </template>
            </VCheckbox>
          </div>
        </div>
      </div>

      <!-- Подвал модалки -->
      <div class="d-flex justify-end pt-2">
        <VBtn
          color="primary"
          class="save-btn text-none font-weight-medium rounded"
          :loading="feedbackStore.isGlobalLoading"
          @click="saveChanges"
        >
          Сохранить
        </VBtn>
      </div>
    </VCard>

    <!-- Диалог подтверждения закрытия без сохранения -->
    <VDialog
      v-model="isConfirmExitOpen"
      max-width="500"
      persistent
      no-click-animation
      style="z-index: 3000;"
    >
      <VCard class="pa-5 bg-surface rounded-lg confirm-exit-subcard">
        <div class="pb-3">
          <h3 class="text-h5 font-weight-medium text-high-emphasis ma-0">
            Несохраненные изменения
          </h3>
        </div>
        <div class="pb-4">
          <p class="text-body-1 text-medium-emphasis m-0">
            Вы внесли изменения в настройку ролей. Вы действительно хотите закрыть окно без сохранения?
          </p>
        </div>
        <div>
          <div class="d-flex justify-end gap-3">
            <VBtn
              variant="outlined"
              class="text-none rounded text-medium-emphasis"
              @click="isConfirmExitOpen = false"
            >
              Отмена
            </VBtn>
            <VBtn
              color="error"
              class="text-none rounded"
              @click="closeWithoutSave"
            >
              Выйти без сохранения
            </VBtn>
          </div>
        </div>
      </VCard>
    </VDialog>
  </VDialog>
</template>

<style lang="scss" scoped>
.role-settings-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important;
}

/* Вкладки «Добавить / Удалить роли» */
.role-action-tabs {
  margin-bottom: 20px;

  :deep(.v-tab) {
    text-transform: none;
    letter-spacing: normal;
    font-size: 15px;
    font-weight: 500;

    .v-tab__slider {
      opacity: 1;
    }
  }

  /* Добавить: default secondary → hover/selected success */
  :deep(.role-action-tab--add.v-tab) {
    color: rgb(var(--v-theme-secondary)) !important;

    .v-btn__content,
    .v-btn__content .v-icon {
      color: rgb(var(--v-theme-secondary)) !important;
    }

    .v-tab__slider {
      background: rgba(var(--v-theme-secondary), 0.24) !important;
    }

    &:hover:not(.v-tab--selected) {
      color: rgb(var(--v-theme-success)) !important;

      .v-btn__content,
      .v-btn__content .v-icon {
        color: rgb(var(--v-theme-success)) !important;
      }

      .v-tab__slider {
        background: rgba(var(--v-theme-success), 0.24) !important;
      }
    }

    &.v-tab--selected {
      color: rgb(var(--v-theme-success)) !important;

      .v-btn__content,
      .v-btn__content .v-icon {
        color: rgb(var(--v-theme-success)) !important;
      }

      .v-tab__slider {
        background: rgb(var(--v-theme-success)) !important;
      }
    }
  }

  /* Удалить: default secondary → hover/selected error */
  :deep(.role-action-tab--delete.v-tab) {
    color: rgb(var(--v-theme-secondary)) !important;

    .v-btn__content,
    .v-btn__content .v-icon {
      color: rgb(var(--v-theme-secondary)) !important;
    }

    .v-tab__slider {
      background: rgba(var(--v-theme-secondary), 0.24) !important;
    }

    &:hover:not(.v-tab--selected) {
      color: rgb(var(--v-theme-error)) !important;

      .v-btn__content,
      .v-btn__content .v-icon {
        color: rgb(var(--v-theme-error)) !important;
      }

      .v-tab__slider {
        background: rgba(var(--v-theme-error), 0.24) !important;
      }
    }

    &.v-tab--selected {
      color: rgb(var(--v-theme-error)) !important;

      .v-btn__content,
      .v-btn__content .v-icon {
        color: rgb(var(--v-theme-error)) !important;
      }

      .v-tab__slider {
        background: rgb(var(--v-theme-error)) !important;
      }
    }
  }
}

.role-action-window {
  overflow: hidden;
}

/* Сетка чекбоксов */
.roles-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.role-column {
  display: flex;
  flex-direction: column;
}

.custom-checkbox {
  :deep(.v-selection-control) {
    min-height: 32px !important;
  }

  :deep(.v-label) {
    font-family: 'Inter', sans-serif !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    color: rgba(var(--v-theme-on-surface), 0.9) !important;
    opacity: 1 !important;
    padding-inline-start: 8px !important;
    white-space: normal !important;
    line-height: 1.3 !important;
  }

  :deep(.v-selection-control__input) {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

/* Нижняя сводка изменений */
.action-summary-container {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.role-summary-chip {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
  color: rgba(var(--v-theme-on-surface), 0.87);
}

/* Кнопка сохранить */
.save-btn {
  width: 140px;
  height: 40px !important;
}

/* Диалог подтверждения выхода */
.confirm-exit-subcard {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important;
}

.max-h-300 {
  max-height: 300px;
}
</style>
